import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Grid, IconButton } from "@mui/material";
import {
    AgentActionButton,
    AgentActionGradientButton,
} from "components/KBo/Conversation/Actions/AgentActionButton";
import AgentStore from "components/KBo/Stores/AgentStore";
import { SmartBanner } from "components/SmartBanner/SmartBanner";
import SuccessDialog from "components/UI/AcxSuccessDialog";
import AcxMenu, { StyledMenuLabel } from "components/UI/Menu/AcxMenu";
import { observer } from "mobx-react";
import React from "react";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import { AcxViewSqlDialog } from "../../../../UI/AcxViewSqlDialog";
import AcxSelectSingle from "../../../../UI/Select/BaseSelectComponents/AcxSelectSingle";
import ConversationsStore, {
    SortOption,
} from "../../../Stores/ConversationsStore";
import EvaluateConversationDialog from "../EvaluateConversationDialog";
import ConversationsOverTime from "./ConversationsOverTime";
import UnfocusedConversation from "./UnfocusedConversation";
import { useCurrentChatStore } from "components/KBo/Stores/useCurrentChat";

const UnfocusedConversationList: React.FC = observer(() => {
    const store = useStore(ConversationsStore);
    const authStore = useStore(AuthStore);
    const agentStore = useStore(AgentStore);
    const currentChatStore = useCurrentChatStore();
    const applicationFiltersStore = useStore(ApplicationFiltersStore);

    const [moreMenuAnchorEl, setMoreMenuAnchorEl] =
        React.useState<null | HTMLElement>(null);
    const [trendAnalysisMenuAnchorEl, setTrendAnalysisMenuAnchorEl] =
        React.useState<null | HTMLElement>(null);
    const [showSqlDialog, setShowSqlDialog] = React.useState(false);

    const sortOptions: SortOption[] = [
        {
            label: <div>Newest First</div>,
            value: "New to Old",
            sortField:
                applicationFiltersStore.dateReferenceOption === "ArrivalDate"
                    ? "ArrivedOn"
                    : "Timestamp",
            sortDirection: "desc",
        },
        {
            label: <div>Oldest First</div>,
            value: "Old to New",
            sortField:
                applicationFiltersStore.dateReferenceOption === "ArrivalDate"
                    ? "ArrivedOn"
                    : "Timestamp",
            sortDirection: "asc",
        },
    ];

    const handleSortSelection = (sortOption: SortOption) => {
        store.setSelectedSortOption(sortOption);
        store.setPageNumberAndGetConversations(1);
    };

    const showSql = store.authStore.isUserUltra() && !!store.hasSqlQueries;
    const canUseSummaryAnalysis = authStore.isAuthcxUser();

    function analyzeTrends(useConversationSummaries: boolean = false) {
        const content =
            useConversationSummaries
                ? "Analyze and Surface Trends using summaries"
                : "Analyze and Surface Trends";
        agentStore.openDrawer();
        currentChatStore.handleSuggestion({
            content,
            action: () =>
                currentChatStore.analyzeCurrentConversationsAction({
                    ConversastionsButton: true,
                    useConversationSummaries,
                }),
            newChatName: "Analyze Current Conversations",
        });
    }

    function handleTrendAnalysisMenuClick(
        event: React.MouseEvent<HTMLButtonElement>,
    ) {
        setTrendAnalysisMenuAnchorEl(event.currentTarget);
    }

    function handleTrendAnalysisMenuClose() {
        setTrendAnalysisMenuAnchorEl(null);
    }

    function handleMoreMenuClick(event: React.MouseEvent<HTMLButtonElement>) {
        setMoreMenuAnchorEl(event.currentTarget);
    }

    function handleMoreMenuClose() {
        setMoreMenuAnchorEl(null);
    }

    return (
        <>
            <Grid
                container
                alignContent="center"
                justifyContent="space-between"
                sx={{ paddingBottom: 2 }}
            >
                <Box
                    fontWeight="bold"
                    fontSize="24px"
                    alignItems={"center"}
                    display={"flex"}
                >
                    {store.isInitialLoad ? "Latest Conversations" : "Results"} (
                    <span data-testid="get-conversations-result">
                        {store.estimatedConversationCount?.toLocaleString()}
                    </span>
                    )
                </Box>
                <Grid
                    container
                    item
                    width="fit-content"
                    alignItems="center"
                    columnGap={2}
                >
                    {store.hasConversations &&
                        authStore.canUserView("View KBo") && (
                            <>
                                <Grid item>
                                    {canUseSummaryAnalysis ? (
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "stretch",
                                            }}
                                        >
                                            <AgentActionButton
                                                fullWidth={false}
                                                id="kbo-analyze-trends"
                                                onClick={() => analyzeTrends()}
                                                sx={{
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0,
                                                }}
                                            >
                                                Analyze Trends
                                            </AgentActionButton>
                                            <AgentActionGradientButton
                                                id="kbo-analyze-trends-dropdown"
                                                aria-haspopup="menu"
                                                aria-label="Analyze trends options"
                                                aria-expanded={
                                                    trendAnalysisMenuAnchorEl
                                                        ? "true"
                                                        : undefined
                                                }
                                                onClick={
                                                    handleTrendAnalysisMenuClick
                                                }
                                                sx={{
                                                    minWidth: 36,
                                                    paddingLeft: 0.75,
                                                    paddingRight: 0.75,
                                                    borderTopLeftRadius: 0,
                                                    borderBottomLeftRadius: 0,
                                                    borderLeft:
                                                        "1px solid rgba(255, 255, 255, 0.35)",
                                                }}
                                            >
                                                <KeyboardArrowDownIcon
                                                    sx={{ fontSize: 18 }}
                                                />
                                            </AgentActionGradientButton>
                                        </Box>
                                    ) : (
                                        <AgentActionButton
                                            fullWidth={false}
                                            onClick={() => analyzeTrends()}
                                            id="kbo-analyze-trends"
                                        >
                                            Analyze Trends
                                        </AgentActionButton>
                                    )}
                                    {canUseSummaryAnalysis && (
                                        <AcxMenu
                                            anchorElement={
                                                trendAnalysisMenuAnchorEl
                                            }
                                            menuItems={[
                                                {
                                                    id: "kbo-analyze-trends-summaries",
                                                    props: {
                                                        onClick: () => {
                                                            handleTrendAnalysisMenuClose();
                                                            analyzeTrends(true);
                                                        },
                                                    },
                                                    label: (
                                                        <StyledMenuLabel>
                                                            Analyze with summaries
                                                        </StyledMenuLabel>
                                                    ),
                                                },
                                            ]}
                                            onMenuClose={
                                                handleTrendAnalysisMenuClose
                                            }
                                        />
                                    )}
                                </Grid>
                            </>
                        )}
                    <Grid
                        container
                        justifyContent="flex-end"
                        item
                        sx={{ width: "fit-content" }}
                    >
                        <AcxSelectSingle
                            id="conversations-sort"
                            options={sortOptions ?? []}
                            valueField={"value"}
                            labelField={"label"}
                            defaultValue={store.selectedSortOption ?? undefined}
                            menuPlacement="bottom"
                            onChange={handleSortSelection}
                            placeholder={"Sort by"}
                            width="120px"
                        />
                    </Grid>
                    {showSql && (
                        <>
                            <IconButton
                                id={`conversations-list-overflow-Menu`}
                                sx={{ color: theme.palette.black.main }}
                                onClick={handleMoreMenuClick}
                                size="small"
                            >
                                <MoreVertIcon />
                            </IconButton>
                            <AcxMenu
                                anchorElement={moreMenuAnchorEl}
                                menuItems={[
                                    {
                                        id: "signals-show-sql-menu-item",
                                        props: {
                                            onClick: () => {
                                                setShowSqlDialog(true);
                                                handleMoreMenuClose();
                                            },
                                            disabled: store.anyTaskLoading,
                                        },
                                        label: (
                                            <StyledMenuLabel>
                                                View SQL
                                            </StyledMenuLabel>
                                        ),
                                    },
                                ]}
                                onMenuClose={handleMoreMenuClose}
                            />
                            <AcxViewSqlDialog
                                sqlStatements={store.sqlQueries!}
                                isModalOpen={showSqlDialog}
                                onClose={() => setShowSqlDialog(false)}
                            />
                        </>
                    )}
                </Grid>
            </Grid>

            <Grid item paddingBottom={2}>
                <SmartBanner title="Why these results?" />
            </Grid>

            <ConversationsOverTime
                data={store.conversationsOverTimeChartData}
                id="unfocused-conversations-page-conversations-over-time"
                isExpanded={store.isConversationsOverTimeChartExpanded}
                setIsExpanded={store.toggleConversationsOverTimeExpanded}
                compareTotal={store.isCompareTotal}
                toggleCompareTotal={() =>
                    (store.isCompareTotal = !store.isCompareTotal)
                }
                showCompareTotal={
                    store.isAppliedFilterCountForLastRequestGreaterThanZero
                }
            />

            {store?.conversationsList?.map((conversation) => {
                return (
                    <UnfocusedConversation
                        conversation={conversation}
                        key={conversation.conversationId}
                    />
                );
            })}

            {/* Evaluate Conversation Confirmation Dialog */}
            {store.evaluateConversationDialogOpen && (
                <EvaluateConversationDialog
                    onConfirmCallback={() => {
                        store.toggleEvaluationSuccessDialogOpen();
                    }}
                />
            )}

            {/* Success Dialog  */}
            {store.evaluateSuccessDialogOpen && (
                <SuccessDialog
                    isOpen={store.evaluateSuccessDialogOpen}
                    onClose={store.toggleEvaluationSuccessDialogOpen}
                    confirmButtonText="Back to Conversations"
                    onConfirm={store.toggleEvaluationSuccessDialogOpen}
                    title="Conversations Sampling"
                    subTitle="You'll be notified when sampling is complete. Reporting data for these evaluations will be available tomorrow."
                />
            )}
        </>
    );
});

export default UnfocusedConversationList;
